import { call, all, put, select } from 'redux-saga/effects';
import * as actions from './actions';
import axios from 'axios';
import { normalizeError } from '../../helpers';
import { push } from 'connected-react-router';
import { getDefaultLandingPathForTenant, getEnvVar } from 'helpers';
import { SESSION_TTL_MS, SESSION_MID_TTL_MS, licenseFeatures } from 'constants.js';
import { actions as campaignActions } from '../campaign';
import { actions as templateActions } from '../template';
import { actions as attachmentTemplateActions } from '../attachmentTemplate';
import { actions as surveyTemplateActions } from '../surveyTemplate';
import { actions as printTemplateActions } from '../printTemplate';
import { actions as recipientQueriesActions } from '../recipientQueryTemplate';
import { actions as conditionQueriesActions } from '../conditionQueryTemplate';
import { actions as reportActions } from '../reports';
import { actions as batchActions } from '../batches';
import { actions as eventReportActions } from '../eventReports';
import { actions as unSubscribeActions } from '../unSubscribe';
import { actions as surveysActions } from '../surveys';
import { actions as settingsActions } from '../settings';
import { actions as userMessageActions } from '../userMessaging';
import { actions as userActions } from '../user';
import { actions as schemaActions } from '../schema';
import { actions as listActions } from '../lists';
import { actions as guestInsightsActions } from '../guestInsights';
import { actions as tableAndToolbarActions } from '../helpers/tableAndToolbar';
import { actions as uploadsActions } from '../uploads';
import sessionEndingModalContent from 'views/UserMessaging/customModalContent/sessionEnding.jsx';
import { getEnrichedSchema } from 'store/enrichedSchema/actions';
import { dismissButton } from 'helpers/error/buttonBuilders';

const { clearCampaignStore, getPaginatedCampaigns } = campaignActions;
const {
  clearTemplateStore,
  getPaginatedTemplates,
  getTemplateTags
} = templateActions;
const {
  clearSurveyTemplateStore,
  getPaginatedSurveyTemplates,
  getSurveyTemplateTags
} = surveyTemplateActions;
const {
  clearAttachmentTemplateStore,
  getPaginatedAttachmentTemplates,
  getAttachmentTags
} = attachmentTemplateActions;
const {
  clearPrintTemplateStore,
  getPaginatedPrintTemplates,
  getPrintTags
} = printTemplateActions;
const {
  clearQueryTemplateStore,
  getPaginatedQueryTemplates
} = recipientQueriesActions;
const {
  clearConditionQueryTemplateStore,
  getPaginatedConditionQueryTemplates
} = conditionQueriesActions;
const { getContextReportData } = reportActions;
const { getPaginatedSurveys, clearSurveyStore } = surveysActions;
const { clearSettingsStore, getAllMetaData } = settingsActions;
const { getUploads, clearUploadsStore, getUsageTypeTags } = uploadsActions;
const { getSchemas } = schemaActions;
const { getPaginatedBatches, clearBatchStore } = batchActions;
const { getPaginatedEventReports, clearEventReportsStore } = eventReportActions;
const { getPaginatedSubscriptions, clearSubscriptionStore } = unSubscribeActions;
const { getPaginatedLists, clearListStore } = listActions;
const { getPaginatedGuestInsights } = guestInsightsActions;
const {
  clearTableAndToolbarStore,
  updateSortBy,
  updateSortOrder
} = tableAndToolbarActions;

export function* clearStore() {
  yield put(clearCampaignStore());
  yield put(clearTemplateStore());
  yield put(clearAttachmentTemplateStore());
  yield put(clearPrintTemplateStore());
  yield put(clearBatchStore());
  yield put(clearEventReportsStore());
  yield put(clearSubscriptionStore());
  yield put(clearSurveyTemplateStore());
  yield put(clearQueryTemplateStore());
  yield put(clearConditionQueryTemplateStore());
  yield put(clearSurveyStore());
  // yield put(clearCommunicationDataStore());
  yield put(clearSettingsStore());
  yield put(clearListStore());
  yield put(clearTableAndToolbarStore());
  yield put(clearUploadsStore());
}

// the actual http calls
const apiRoot = `${window._env_?.REACT_APP_API_PATH}`;
const baseRoot = `${window._env_?.REACT_APP_BASENAME}`;

const getSsoServiceApiRoot = () =>
  `${(window._env_ && window._env_.REACT_APP_USER_API_ROOT) ||
    process.env.REACT_APP_USER_API_ROOT ||
    ''}/authorization-service`;

const getAuthServiceApiRoot = () =>
  `${(window._env_ && window._env_.REACT_APP_USER_API_ROOT) ||
    process.env.REACT_APP_USER_API_ROOT ||
    ''}/auth-service/auth`;

const getUserServiceApiRoot = () =>
  `${(window._env_ && window._env_.REACT_APP_USER_API_ROOT) ||
    process.env.REACT_APP_USER_API_ROOT ||
    ''}/user-service`;

const getUserServiceV2ApiRoot = () =>
  `${(window._env_ && window._env_.REACT_APP_USER_API_ROOT) ||
    process.env.REACT_APP_USER_API_ROOT ||
    ''}/user-service/v2`;

const getLicensesApiRoot = (tenantId, businessContextId) =>
  `${apiRoot}license/tenants/${tenantId}/context/${businessContextId}/v1/feature`;

const getContentServiceApiRoot = (tenantId, businessContextId) =>
  `${(window._env_ && window._env_.REACT_APP_USER_API_ROOT) ||
    process.env.REACT_APP_USER_API_ROOT ||
    ''}/content-service/content/tenants/${tenantId}/properties/${businessContextId}/contents`;

export const loginAsync = (username, password, forceLogin) =>
  axios
    .post(`${getAuthServiceApiRoot()}/users/login`, {
      username,
      password,
      ...(forceLogin && { forceLogin: true }),
      populateValidTenants: true
    })
    .then(response => response.data)
    .catch(err => {
      throw err;
    });

export const ssoTokenAsync = adsamlDetails => {
  let { adsamlToken, tenantId, contextId } = adsamlDetails;
  let tokenData = new FormData();
  tokenData.append('token', adsamlToken);
  return axios({
    method: 'post',
    url: `${getSsoServiceApiRoot()}/login/v2/tenants/${tenantId}/context/${contextId}/adsaml/signin`,
    data: tokenData,
    headers: { 'Content-Type': undefined }
  })
    .then(response => response.data)
    .catch(err => {
      throw err;
    });
};
export const logoutAsync = xToken =>
  axios
    .delete(
      `${getAuthServiceApiRoot()}/users/login`,
      xToken && {
        headers: {
          'x-token': xToken
        }
      }
    )
    .then(response => {
      return response;
    })
    .catch(err => {
      throw err;
    });

export const loginWithCode = code =>
  axios
    .post(
      `${getAuthServiceApiRoot()}/tenants/${window.localStorage.getItem(
        'GCM-tenantId'
      )}/users/login/code`,
      {
        tenantId: window.localStorage.getItem('GCM-tenantId'),
        token: window.localStorage.getItem('GCM-xToken'),
        restrictedLoginCode: '',
        code: code,
        contextId: '',
        populateValidTenants: true
      }
    )
    .then(response => response.data)
    .catch(err => {
      throw err;
    });

export const getLoginInfoAsync = (xtoken, tenantId) =>
  axios
    .get(`${getAuthServiceApiRoot()}/tenants/${tenantId}/users/loginInfo`, {
      headers: {
        'x-token': xtoken
      }
    })
    .then(response => response.data)
    .catch(err => {
      throw err;
    });

export const getLoginDetailsAsync = (tenantId, isSamlUser) =>
  axios
    .get(
      `${getUserServiceV2ApiRoot()}/user/tenants/${tenantId}/users/details?isSamlUser=${isSamlUser ||
        false}`
    )
    .then(response => response.data)
    .catch(err => {
      throw err;
    });

export const getUserSummaryAsync = tenantId =>
  axios
    .get(`${getUserServiceApiRoot()}/tenant/tenants/${tenantId}/thirdPartyTenantAccessSummary`)
    .then(response => response.data)
    .catch(err => {
      throw err;
    });

export const getUserTokenAsync = tenantId =>
  axios
    .get(
      `${getAuthServiceApiRoot()}/tenants/${tenantId}/userPermissions/userToken`
    )
    .then(response => response.data)
    .catch(err => {
      throw err;
    });

export const getUserPropertyAsync = tenantId =>
  axios
    .get(`${getUserServiceApiRoot()}/businessContext/tenants/${tenantId}`)
    .then(response => response.data)
    .catch(err => {
      throw err;
    });

export const getTenantDetailsAsync = tenantId =>
  axios
    .get(`${getUserServiceApiRoot()}/tenant/tenants/${tenantId}`)
    .then(response => response.data)
    .catch(err => {
      throw err;
    });

export const checkAdsamlAsync = (tenantId, contextId) =>
  axios
    .get(
      `${getSsoServiceApiRoot()}/api/providerConfig/tenants/${tenantId}/context/${contextId}/isAdsamlConfigured`
    )
    .then(response => response.data)
    .catch(err => {
      throw err;
    });

export const resetPasswordAsync = (username, updatedPassword) =>
  axios
    .put(
      `${getUserServiceApiRoot()}/user/users/${username}/password`,
      updatedPassword
    )
    .then(response => response.data)
    .catch(err => {
      throw err;
    });

export const forgotPasswordV2Async = username =>
  axios
    .get(
      `${getUserServiceV2ApiRoot()}/user/users/${username}/usersecuritynotificationoptions`
    )
    .then(response => response.data)
    .catch(err => {
      throw err;
    });


export const forgotUsernameAsync = data =>
  axios
    .post(`${getUserServiceApiRoot()}/user/v2/sendmessageforgotusername`, data)
    .then(response => response.data)
    .catch(err => {
      throw err;
    });

export const sendForgotPasswordV2Async = (username, data) =>
  axios
    .post(
      `${getUserServiceV2ApiRoot()}/user/users/${username}/sendmessageforgotpassword`,
      data
    )
    .then(response => response.data)
    .catch(err => {
      throw err;
    });

export const getLicensesAsync = async (tenantId, businessContextId, xToken) =>
  axios
    .get(`${getLicensesApiRoot(tenantId, businessContextId)}/all`, {
      headers: {
        'x-token': xToken
      }
    })
    .then(response => response.data)
    .catch(err => {
      throw err;
    });

const refreshTokenAsync = tenantId =>
  axios
    .get(`${getUserServiceApiRoot()}/tenant/tenants/${tenantId}`, {})
    .then(response => response.data)
    .catch(err => {
      throw err;
    });

export const getLogoDetailsAsync = tenantId =>
  axios
    .get(`${getUserServiceApiRoot()}/tenant/tenants/${tenantId}/logoDetails`)
    .then(response => response.data)
    .catch(err => {
      throw err;
    });

export const sendCodeThroughSmsAsync = username =>
  axios
    .post(
      `${getUserServiceApiRoot()}/user/tenants/${window.localStorage.getItem(
        'GCM-tenantId'
      )}/users/${username}/sendmessagesecondfactorcode`,
      {}
    )
    .then(response => response.data)
    .catch(err => {
      throw err;
    });

export const sendCodeThroughEmailAsync = username =>
  axios
    .post(
      `${getUserServiceApiRoot()}/user/tenants/${window.localStorage.getItem(
        'GCM-tenantId'
      )}/users/${username}/sendemailsecondfactorcode`,
      {}
    )
    .then(response => response.data)
    .catch(err => {
      throw err;
    });

export const getTurnstileValidationAsync = () =>
  axios
    .get(
      `${baseRoot}siteverify?captchaToken=${window.localStorage.getItem(
        'turnstile_token'
      )}`
    )
    .then(response => response.data)
    .catch(err => {
      throw err;
    });

export const getTenantListAsync = () =>
  axios
    .get(`${getUserServiceApiRoot()}/tenant/tenants`)
    .then(response => response.data)
    .catch(err => {
      throw err;
    });

//validators
function checkEmailOrSmsProvided(account) {
  let checkSmsProvided = account.smsProvided ? 'SMS' : '';
  return account.emailProvided ? 'Email' : checkSmsProvided;
}
const validateAccountDetails = account => {
  let validation = {};
  if (account.recoveryLocked) {
    validation.error = 'Your account is temporarily locked.';
  } else {
    validation.questions = account.securityQuestion.map(question => {
      question.answer = '';
      return question;
    });
    validation.notificationProvided = checkEmailOrSmsProvided(account);
  }
  return validation;
};

// selectors
const currentUser = store => store.user.current;
const currentTenant = store => store.user.selectedTenant;
const loggedTenant = store => store.user.loggedTenant;
const currentLoginDetails = store => store.user.loginDetails;
const currentBusinessContext = store => store.user.selectedBusinessContext;
const currentToken = store => store.user.xToken;
const currentUrl = store => store.router.location.pathname;
const currentHasPasswordExpired = store => store.user.hasPasswordExpired;
const currentAdsamlUser = store => store.user.adsamlDetails;
const currentUsername = store => store.user.username;
const currentLogoDetails = store => store.user.logoDetails;
const rowsPerPage = store => store.user.selectedRowsPerPage;
const translateLanguageContent = store => store.userMessaging.translateLanguageContent;

export function* loginAsSupportUser(action) {
  const {
    tenantId,
    loginData,
    thirdPartyTenantContextRoles,
    loginDetails
  } = action.data;
  let userToken = yield call(getUserTokenAsync, tenantId);
  let userproperties = yield call(getUserPropertyAsync, tenantId);
  yield put(actions.setContextDetails(userproperties));
  let tenantDetails = yield call(getUserSummaryAsync, tenantId);
  let contexts = [];
  let contextIds = [];
  let contextRoles = [];
  if (loginData.tenants[0].tenantId === tenantId) {
    contextIds = loginData.tenants[0].contextResponses.map(
      tenant => tenant.contextId
    );
    contextRoles = loginDetails.contextRoles;
  } else {
    contextIds = thirdPartyTenantContextRoles[tenantId]?.map(
      tenant => tenant.contextId
    );
    contextRoles = thirdPartyTenantContextRoles[tenantId];
  }
  contextIds.map(contextId =>
    contexts.push({
      businessContextId: contextId,
      contextName:
        contextId === 'Default'
          ? 'ALL'
          : userproperties.find(
              property => property.businessContextId === contextId
            ).name
    })
  );
  let supportLoginData = {
    token: loginData.token,
    secondFactorRequired: loginData.secondFactorRequired,
    selectedTenantId: tenantId,
    tenants: [
      {
        tenantId: tenantDetails.tenantId,
        tenantName: tenantDetails.name,
        tenantType: tenantDetails.tenantType,
        defaultPermissions: userToken.tenantAuthority.contextPermissionMap,
        contextResponses: contexts,
        isSupportUser: true,
        contextRoles
      }
    ]
  };
  yield put(actions.setSelectedTenant(tenantId));
  yield put(actions.supportUserLoginSucceeded(supportLoginData));
  yield put(actions.getLicenses());
  yield put(actions.getLogoDetails());
  yield put(actions.loginSuccess());
}

export function* loginWithSupportAccess(action) {
  const sessionToken = window.localStorage.getItem('GCM-xToken');
  if (action.data === window.localStorage.getItem('GCM-tenantId')) {
    yield put(actions.getLicenses());
    yield put(actions.loginSuccess());
  } else {
    window.localStorage.removeItem('GCM-tenantId');
    window.localStorage.setItem('GCM-tenantId', action.data);
    yield put(actions.setSelectedTenant(action.data));
    try {
      let loginData = yield call(getLoginInfoAsync, sessionToken, action.data);
      if (loginData.token) {
        let loginDetails = yield call(
          getLoginDetailsAsync,
          action.data,
          loginData.samlUser
        );
        yield put(actions.gettingLoginDetailsSucceeded(loginDetails));

        yield put(
          actions.loginAsSupportUser({
            tenantId: action.data,
            loginData: loginData,
            thirdPartyTenantContextRoles:
              loginDetails.thirdPartyTenantContextRoles,
            loginDetails
          })
        );
      }
    } catch (ex) {
      const languageContent = yield select(translateLanguageContent);
      yield put(actions.loggingInFailed(normalizeError(ex, {}, languageContent)));
    }
  }
}

export function* loginIfNeeded(action) {
  try {
    if (action.options?.clearSessionData) {
      yield clearStore();
      window.localStorage.removeItem('GCM-xToken');
      window.localStorage.removeItem('GCM-tenantId');
      window.localStorage.removeItem('GCM-businessContextId');
    }

    let turnstileResponse = null;
    if (
      window._env_ &&
      window._env_.ADM_INCLUDE_TURNSTILE_INTEGRATION === 'true' &&
      action.username
    ) {
      turnstileResponse = yield call(getTurnstileValidationAsync);
      window.localStorage.setItem('isTurnstileRefreshNeeded', true);
      if (
        !turnstileResponse.success &&
        turnstileResponse['error-codes'].length > 0
      ) {
        yield put(
          actions.loggingInFailed({
            userMessage: {
              instructions: `Failed due to ${turnstileResponse['error-codes'][0]}`,
              severity: 'ERROR',
              title: 'Error'
            },
            raw: turnstileResponse
          })
        );
      }
    }

    if (
      turnstileResponse === null ||
      (turnstileResponse && turnstileResponse.success)
    ) {
      const xToken = yield select(currentToken);
      let loginData = yield select(currentUser);
      const sessionToken = window.localStorage.getItem('GCM-xToken');
      const lastTenantId = window.localStorage.getItem('GCM-tenantId');
      let lastBusinessContext = window.localStorage.getItem(
        'GCM-businessContextId'
      );

      let userproperties;
      if (!xToken || action.options?.clearSessionData) {
        yield put(actions.loggingIn());
        if (action.username && action.password) {
            loginData = yield call(
              loginAsync,
              action.username,
              action.password,
              action.options?.forceLogin
            );
        } else if (sessionToken && lastTenantId) {
          loginData = yield call(getLoginInfoAsync, sessionToken, lastTenantId);
        } else {
          yield put(
            actions.loggingInFailed({
              message: 'Unable to sign in or restore session',
              raw: {}
            })
          );
          yield put(actions.logout());
          return;
        }
      }

      if (loginData.token) {
        const availableTenantIds = (loginData.tenants || []).map(tenant =>
          String(tenant?.tenantId)
        );
        const storedTenantId = window.localStorage.getItem('GCM-tenantId');
        let effectiveTenantId =
          storedTenantId ||
          String((yield select(currentTenant))?.tenantId || '');

        if (!effectiveTenantId && availableTenantIds.length > 0) {
          effectiveTenantId = availableTenantIds[0];
        }

        if (effectiveTenantId && String(storedTenantId) !== String(effectiveTenantId)) {
          window.localStorage.setItem('GCM-tenantId', effectiveTenantId);
        }

        yield put(actions.loggingInSucceeded(loginData));
        window.localStorage.setItem('GCM-xToken', loginData.token);
        if (loginData.secondFactorRequired) {
          window.localStorage.setItem(
            'GCM-tenantId',
            (yield select(currentTenant)).tenantId
          );
          yield put(actions.getAuthentication(loginData));
        } else {
          if (!action.isSsoLogin) {
            if (lastTenantId && lastBusinessContext) {
              yield put(actions.setSelectedTenant(effectiveTenantId || lastTenantId));
              yield put(actions.setSelectedBusinessContext(lastBusinessContext));
            } else {
              window.localStorage.setItem(
                'GCM-tenantId',
                effectiveTenantId || (yield select(currentTenant)).tenantId
              );
              window.localStorage.setItem(
                'GCM-businessContextId',
                (yield select(currentBusinessContext))?.businessContextId
              );
            }
          }
          let userToken;
          if (loginData.tenants[0]?.tenantId === '0' && loginData.tenants[0]?.defaultPermissions?.includes('Platform_readTenants')) {
            yield put(actions.getTenantList());
          }
          else if(loginData.tenants[0]?.tenantType === "SERVICE_ACCOUNT") {
            userToken = yield call(getUserTokenAsync, effectiveTenantId || window.localStorage.getItem('GCM-tenantId'));
            yield put(actions.getTenantList(loginData.tenants[0]?.tenantId));
          }
          const primaryTenantId = effectiveTenantId || window.localStorage.getItem('GCM-tenantId');
          let contextTenantId = primaryTenantId;

          try {
            userproperties = yield call(getUserPropertyAsync, contextTenantId);
            yield put(actions.setContextDetails(userproperties));
          } catch (tenantScopedError) {
            const fallbackTenantId = availableTenantIds.find(
              tenantId => String(tenantId) !== String(contextTenantId)
            );

            if (tenantScopedError?.response?.status === 404 && fallbackTenantId) {
              contextTenantId = fallbackTenantId;
              window.localStorage.setItem('GCM-tenantId', contextTenantId);
              if (!action.isSsoLogin) {
                yield put(actions.setSelectedTenant(contextTenantId));
              }

              userproperties = yield call(getUserPropertyAsync, contextTenantId);
              yield put(actions.setContextDetails(userproperties));
            } else {
              throw tenantScopedError;
            }
          }

          const loginDetailsTenantId =
            loginData.tenants[0]?.tenantType === 'SERVICE_ACCOUNT' &&
            loginData.tenants[0]?.tenantId !== undefined
              ? String(loginData.tenants[0]?.tenantId)
              : contextTenantId;

          let loginDetails;
          try {
            loginDetails = yield call(
              getLoginDetailsAsync,
              loginDetailsTenantId,
              loginData.samlUser
            );
          } catch (loginDetailsError) {
            if (
              loginDetailsError?.response?.status === 404 &&
              loginDetailsError?.response?.data?.code === 1002
            ) {
              loginDetails = {
                ...loginData,
                tenantId: contextTenantId,
                contextRoles: loginData?.contextRoles || [],
                thirdPartyTenantContextRoles:
                  loginData?.thirdPartyTenantContextRoles || {}
              };
            } else {
              throw loginDetailsError;
            }
          }
          if (action.isSsoLogin) {
            lastBusinessContext = window.localStorage.getItem('GCM-businessContextId');
          }
          loginDetails.businessContextId = lastBusinessContext;
          loginDetails.thirdPartyTenantContextPermissions = userToken?.tenantAuthority?.contextPermissionMap;
          yield put(actions.gettingLoginDetailsSucceeded(loginDetails));
          yield put(actions.loginSuccess());
          yield put(actions.getLicenses());
          yield put(actions.getLogoDetails());
        }
      } else {
        yield put(
          actions.loggingInFailed({
            message: 'Sign in response not recognized',
            raw: loginData
          })
        );
      }
    }
  } catch (ex) {
    const languageContent = yield select(translateLanguageContent);
    let concurrentObj = {
      concurrentStatus: ex.response.status,
      concurrentCode: ex.response.data?.code
    };

    const combinedError = {
      ...normalizeError(ex, {}, languageContent),
      ...concurrentObj
    };
    
    yield put(actions.loggingInFailed(combinedError));
  }
}

export function* getSsoLogin(action) {
  let loginData;
  try {
    if (action.adsamlDetails.adsamlToken) {
      loginData = yield call(ssoTokenAsync, action.adsamlDetails);
      if (loginData?.token) {
        window.localStorage.setItem(
          'GCM-tenantId',
          action.adsamlDetails.tenantId
        );
        window.localStorage.setItem(
          'GCM-businessContextId',
          action.adsamlDetails.contextId
        );
        window.localStorage.setItem('GCM-xToken', loginData.token);
        yield call(loginIfNeeded, { isSsoLogin: true });
      }
    }
  } catch (ex) {
    const languageContent = yield select(translateLanguageContent);
    yield put(actions.loggingInFailed(normalizeError(ex, {}, languageContent)));
  }
}

export function* getSupportUsers(action) {
  try {
    let options = [];
    let summaryDetails = yield all(
      action.data.map(tenantId => {
        return call(getUserSummaryAsync, tenantId);
      })
    );
    summaryDetails.map(summary => {
      options.push({ label: summary.name, value: summary.tenantId });
    });
    yield put(actions.gettingSupportUsersSucceeded(options));
  } catch (ex) {
    yield put(actions.gettingSupportUsersFailed(ex));
  }
}

export function* checkAdsaml(action) {
  let adsamlLogin = yield select(currentAdsamlUser);
  let isAdsamlEnabled = false;
  try {
    isAdsamlEnabled = yield call(
      checkAdsamlAsync,
      adsamlLogin.tenantId,
      'Default'
    );
    if (isAdsamlEnabled) {
      yield put(actions.checkingAdsamlSucceeded(isAdsamlEnabled));
    } else {
      yield put(
        actions.checkingAdsamlFailed('Adsaml is not configured for this user')
      );
    }
  } catch (ex) {
    yield put(actions.checkingAdsamlFailed(ex));
  }
}

export function* callRAuth(action) {
  let adsamlLogin = yield select(currentAdsamlUser);
  try {
    const form = document.createElement('form');
    form.method = 'post';
    form.action = `${getSsoServiceApiRoot()}/login/v2/tenants/${
      adsamlLogin.tenantId
    }/context/Default/client/${getEnvVar('ADM_ADSAML_CLIENT_ID')}/adsaml`;
    document.body.appendChild(form);
    form.submit();
  } catch (ex) {
    yield put(actions.checkingAdsamlFailed(ex));
  }
}

export function* resetPassword(action) {
  let hasPasswordExpired = yield select(currentHasPasswordExpired);
  let username = yield select(currentUsername);
  let resetPasswordData = {};
  try {
    if (hasPasswordExpired) {
      resetPasswordData = yield call(
        resetPasswordAsync,
        username,
        action.updatedPassword
      );
    }
  } catch (ex) {
    const languageContent = yield select(translateLanguageContent);
    yield put(actions.resetPasswordFailed(normalizeError(ex, {}, languageContent)));
  }

  if (resetPasswordData.token) {
    yield put(actions.login(username, action.updatedPassword.newPassword, { forceLogin: true }));
  }
}

export function* forgotPassword(action) {
  let forgotPasswordData = {};
  let updateData = {};
  try {
    forgotPasswordData = yield call(forgotPasswordV2Async, action.username);
    updateData = validateAccountDetails(forgotPasswordData);
      yield put(actions.forgotPasswordSucceeded(updateData));
      yield put(actions.getSecurityQuestionSucceeded(updateData.questions));
  } catch (ex) {
    const languageContent = yield select(translateLanguageContent);
    yield put(actions.forgotPasswordFailed(normalizeError(ex, {}, languageContent)));
  }
}

export function* forgotUsername(action) {
  let forgotUsernameData = {};
  try {
    forgotUsernameData = yield call(forgotUsernameAsync, action.data);
    if (forgotUsernameData.message) {
      yield put(actions.forgotUsernameSucceeded(forgotUsernameData.message));
    } else {
      yield put(
        actions.forgotUsernameFailed({
          message: 'An error has occurred. Please try again or contact support.',
          raw: forgotUsernameData
        })
      );
    }
  } catch (ex) {
    const languageContent = yield select(translateLanguageContent);
    yield put(actions.forgotUsernameFailed(normalizeError(ex, {}, languageContent)));
  }
}

export function* sendForgotPassword(action) {
  let sendForgotPasswordData = {};
  let username = yield select(currentUsername);
  try {
    sendForgotPasswordData = yield call(
      sendForgotPasswordV2Async,
      username,
      action.option
    );
    if (sendForgotPasswordData.success) {
      yield put(actions.sendForgotPasswordSucceeded(sendForgotPasswordData));
    } else {
      yield put(
        actions.sendForgotPasswordFailed({
          message: 'Sorry our service is currently unavailable.',
          raw: null
        })
      );
    }
  } catch (ex) {
    const languageContent = yield select(translateLanguageContent);
    yield put(actions.sendForgotPasswordFailed(normalizeError(ex, {}, languageContent)));
  }
}

export function* getAuthenticationData() {
  yield put(push('/authentication'));
}

export function* loginWithAuthentication(action) {
  try {
    let loginData = yield call(loginWithCode, action.code);
    const lastTenantId = window.localStorage.getItem('GCM-tenantId');
    const lastBusinessContext = window.localStorage.getItem(
      'GCM-businessContextId'
    );
    if (loginData.token) {
      yield put(actions.loggingInSucceeded(loginData));
      yield put(actions.loginSuccess());
      window.localStorage.setItem('GCM-xToken', loginData.token);
      if (lastTenantId && lastBusinessContext) {
        yield put(actions.setSelectedTenant(lastTenantId));
        yield put(actions.setSelectedBusinessContext(lastBusinessContext));
      } else {
        window.localStorage.setItem(
          'GCM-tenantId',
          (yield select(currentTenant)).tenantId
        );
        window.localStorage.setItem(
          'GCM-businessContextId',
          (yield select(currentBusinessContext))?.businessContextId
        );
      }
      let userproperties = yield call(
        getUserPropertyAsync,
        window.localStorage.getItem('GCM-tenantId')
      );
      yield put(actions.setContextDetails(userproperties));
      let loginDetails = yield call(
        getLoginDetailsAsync,
        window.localStorage.getItem('GCM-tenantId'),
        loginData.samlUser
      );
      yield put(actions.gettingLoginDetailsSucceeded(loginDetails));
      yield put(actions.getLicenses());
      yield put(actions.getLogoDetails());
    } else {
      yield put(
        actions.loggingInFailed({
          message: 'Sign in response not recognized',
          raw: loginData
        })
      );
    }
  } catch (ex) {
    const languageContent = yield select(translateLanguageContent);
    yield put(actions.proceedAuthenticationFailed(normalizeError(ex, {}, languageContent)));
  }
}

export function* sendCodeThroughSms(action) {
  try {
    let sendCodeStatus = yield call(sendCodeThroughSmsAsync, action.username);
    yield put(actions.updateSendCodeStatus(sendCodeStatus, 'sms'));
  } catch (ex) {
    const languageContent = yield select(translateLanguageContent);
    yield put(
      actions.updateSendCodeStatus(normalizeError(ex, {}, languageContent).userMessage.title, 'sms')
    );
  }
}

export function* sendCodeThroughEmail(action) {
  try {
    let sendCodeStatus = yield call(sendCodeThroughEmailAsync, action.username);
    yield put(actions.updateSendCodeStatus(sendCodeStatus, 'email'));
  } catch (ex) {
    const languageContent = yield select(translateLanguageContent);
    yield put(
      actions.updateSendCodeStatus(
        normalizeError(ex, {}, languageContent).userMessage.title,
        'email'
      )
    );
  }
}

export function* refreshToken() {
  try {
    const lastTenantId = window.localStorage.getItem('GCM-tenantId');
    yield call(refreshTokenAsync, lastTenantId);
    yield put(actions.refreshTokenSucceeded());
    yield put(actions.sessionStart());
  } catch (ex) {
    yield put(actions.refreshTokenFailed(ex));
  }
}

export function* refreshSessionToken() {
  try {
    const lastTenantId = window.localStorage.getItem('GCM-tenantId');
    yield call(refreshTokenAsync, lastTenantId);
    yield put(actions.refreshTokenSucceeded());
  } catch (ex) {
    yield put(actions.refreshTokenFailed(ex));
  }
}

let timer;
export function* sessionStart() {
  if (timer) {
    clearTimeout(timer);
  }
  let admSessionMidIdleTimeMins = getEnvVar('ADM_SESSION_MID_IDLE_TIME_MINS');
  let firstTimer = admSessionMidIdleTimeMins ? admSessionMidIdleTimeMins * 60 * 1000 : SESSION_MID_TTL_MS;
  yield call(
    () =>
      new Promise(resolve => {
        timer = setTimeout(resolve, firstTimer);
      })
  );

  yield put(actions.refreshSessionToken());
  let admSessionIdleTimeMins = getEnvVar('ADM_SESSION_IDLE_TIME_MINS');
  let idleTimer = admSessionIdleTimeMins ? admSessionIdleTimeMins * 60 * 1000 : SESSION_TTL_MS;
  yield call(
    () =>
      new Promise(resolve => {
        timer = setTimeout(resolve, idleTimer);
      })
  );

  yield put(actions.sessionEnding());
}

export function* sessionEnding() {
  yield put(
    userMessageActions.setMessage({
      customContent: sessionEndingModalContent,
      buttons: [
        { title: 'Logout', action: 'LOGOUT' },
        { title: 'Continue', action: ['DISPATCH', actions.refreshToken()] }
      ]
    })
  );
}

export function* handleStoreClearing() {
  const url = yield select(currentUrl);
  let pathToNavigate = url.split('/')[1] || 'campaigns';

  try {
    yield clearStore();
  } catch (ex) {
    console.log(ex);
  }

  switch (pathToNavigate) {
    case 'campaigns':
      yield put(getPaginatedCampaigns({ force: true }));
      break;
    case 'templates':
      yield put(getPaginatedTemplates({ force: true }));
      yield put(getTemplateTags());
      break;
    case 'attachment-templates':
      yield put(getPaginatedAttachmentTemplates({ force: true }));
      yield put(getAttachmentTags());
      break;
    case 'surveyResultResponse-templates':
      yield put(getPaginatedSurveyTemplates({ force: true }));
      yield put(getSurveyTemplateTags());
      break;
    case 'print-templates':
      yield put(getPaginatedPrintTemplates({ force: true }));
      yield put(getPrintTags());
      break;
    case 'recipient-queries':
      yield put(getPaginatedQueryTemplates({ force: true }));
      break;
    case 'condition-queries':
      yield put(getPaginatedConditionQueryTemplates({ force: true }));
      break;
    case 'lists':
      yield put(getPaginatedLists({ force: true }));
      break;
    case 'reports':
      yield put(getContextReportData());
      break;
    case 'batches':
      yield put(getPaginatedBatches({ force: true }));
      break;
    case 'events':
      yield put(getPaginatedEventReports({ force: true }));
      break;
    case 'messages':
      pathToNavigate = 'events';
      yield put(updateSortBy('createTime'));
      yield put(updateSortOrder('DESC'));
      yield put(getPaginatedEventReports({ force: true }));
      break;
    case 'unsubscribe':
      yield put(getPaginatedSubscriptions({ force: true }));
      break;
    case 'surveys':
      yield put(getPaginatedSurveys({ force: true }));
      break;
    case 'settings':
      yield put(getAllMetaData());
      break;
    case 'schema':
      yield put(getSchemas({ force: true }));
      break;
    case 'enriched-schema':
      yield put(getEnrichedSchema({ force: true, showActive: true }));
      break;
    case 'guest-insights':
      yield put(getPaginatedGuestInsights({ force: true }));
      break;
    case 'uploads':
      yield put(getUploads({ force: true }));
      yield put(getUsageTypeTags());
      break;
  }

  yield put(push(`/${pathToNavigate}`));
}

export function* logoutIfNeeded(action) {
  try {
    const xToken = yield select(currentToken);
    const samlUser = yield select(store => store.user.current.samlUser);    
    const tenantDetails = yield select(store => store.user.tenantDetails);    
    if (xToken) {
      yield put(actions.loggingOut());
      let logoutResponse = yield call(logoutAsync, xToken);
      if (logoutResponse.status === 204 && !samlUser) {
        yield call(adsamlLogout,tenantDetails);
      }
    }
    yield clearStore();
    yield put(actions.loggingOutSucceeded(action)); // logout returns 204.
  } catch (ex) {
    const languageContent = yield select(translateLanguageContent);
    yield put(actions.loggingOutFailed(normalizeError(ex, {}, languageContent)));
  }
}

export function* getTenantDetails(action) {
  try {   
    const xToken = window.localStorage.getItem('GCM-xToken');
    let tenantDetails = yield call(getTenantDetailsAsync, action,xToken);   
    yield put(actions.gettingTenantDetailsSucceeded(tenantDetails));    
  } catch(ex) {
    console.log(ex);
  }
}

export function* adsamlLogout(action) {      
  if (action?.tenantDetails?.logoutType === 'BOTH') {
    let contextId = 'Default';
    let tenantId = action?.tenantDetails?.tenantId;
    let url = `/authorization-service/logout/tenants/${tenantId}/context/${contextId}/client/${getEnvVar('ADM_ADSAML_CLIENT_ID')}/adsaml`;
    var iframe = document.createElement('iframe');
    iframe.style.display = 'none';
    document.body.appendChild(iframe);
    var form = document.createElement('form');
    form.method = 'post';
    form.action = url;
    form.target = iframe.name;
    if (iframe && iframe.contentDocument) {
      iframe.contentDocument.body.appendChild(form);
    }
    form.submit();
  }
 
}

export function* getLicensesData(action) {
  let tenantId, businessContextId;
  if(action?.data?.isTenantSwitch) {
    tenantId = action?.data?.tenantId;
    businessContextId = 'Default'
  } else {
    tenantId = (yield select(currentTenant)).tenantId;
    let context = yield select(currentBusinessContext);
    businessContextId = context?.businessContextId || 'Default';
  }
  const xToken = yield select(currentToken);
  const licensePopup = userMessageActions.setMessage({
      title: 'Warning',
      severity: 'warning',
      instructions: `Tenant "${action?.data?.tenantName}" doesn't have a valid license to access ADM.`,
      buttons: [dismissButton('Cancel')]
    });
  try {
    if(action?.data?.isTenantSwitch && (yield select(loggedTenant)).tenantType === "SERVICE_ACCOUNT") {
      let userToken = yield call(getUserTokenAsync, action?.data?.tenantId);
      businessContextId = Object.keys(userToken?.tenantAuthority.contextPermissionMap)[0] || 'Default';
    }
    const defaultLandingPath = getDefaultLandingPathForTenant(tenantId);
    let data = yield call(
      getLicensesAsync,
      tenantId,
      businessContextId,
      xToken
    );
    if(action?.data?.isTenantSwitch) {
      let plan = Object.keys(data?.licenses);
      let features = data?.licenses[plan];
      if (
        features.includes(licenseFeatures.FEATURE_ALL) || 
        features.includes(licenseFeatures.FEATURE_BASIC) || 
        features.includes(licenseFeatures.FEATURE_STANDARD)
      ) {
        yield put(actions.gettingLicensesSucceeded(data));
        yield put(actions.loginReady());
        yield put(userActions.setSelectedTenant(action?.data?.tenantId, true));
        yield put(push(defaultLandingPath));
      } else {
        yield put(licensePopup);
      }
    } else {
      yield put(actions.gettingLicensesSucceeded(data));
      yield put(actions.loginReady());
    }
  } catch (ex) {
    if (ex?.response?.status === 403) {
      yield put(actions.gettingLicensesFailed(ex));
    } else if (ex?.response?.status === 400) {
      if(action?.data?.isTenantSwitch) {
        yield put(licensePopup);
      } else {
        yield put(actions.gettingLicensesFailed({ ex, hasNoLicense: true }));
      }
    } else {
      yield put(actions.gettingLicensesFailed({ ex, errorOccurred: true }));
    }
  }
}

export function* getLogoDetails() {
  let tenantId = (yield select(currentTenant)).tenantId;
  let businessContextId = yield select(currentBusinessContext)
    ?.businessContextId || 'Default';
  try {
    let data = yield call(getLogoDetailsAsync, tenantId);
    yield put(actions.gettingLogoDetailsSucceeded(data));

    let logoId = (yield select(currentLogoDetails)).logoContentId;

    if (logoId) {
      let logoUrl = `${getContentServiceApiRoot(
        tenantId,
        businessContextId
      )}/images/${logoId}`;

      localStorage.setItem('url', logoUrl);
    } else {
      localStorage.removeItem('url');
    }
  } catch (ex) {
    yield put(actions.gettingLogoDetailsFailed(ex));
    localStorage.removeItem('url');
  }
}

export function* getTenantList(action) {
  try {
    let tenantListData;
    if(action.data) {
      tenantListData = yield call(getUserSummaryAsync, action.data);
    } else {
      tenantListData = yield call(getTenantListAsync);
    }
    yield put(actions.gettingTenantListSucceeded(tenantListData));
  } catch (ex) {
    console.log(ex);
    yield put(actions.gettingTenantListFailed(ex));
  }
}

export function* getSelectedTenantContextDetails(tenantId) {
  try {
    const lastBusinessContext = window.localStorage.getItem(
      'GCM-businessContextId'
    );
    const existingLoginDetails = yield select(currentLoginDetails);
    let loginData = yield select(currentUser);
    let userproperties = yield call(getUserPropertyAsync, tenantId);
    yield put(actions.setContextDetails(userproperties));
    let userToken;
    if((yield select(loggedTenant)).tenantType === "SERVICE_ACCOUNT") {
      userToken = yield call(getUserTokenAsync, window.localStorage.getItem('GCM-tenantId'));
    }
    let loginDetails;
    try {
      loginDetails = yield call(
        getLoginDetailsAsync,
        tenantId,
        loginData?.samlUser
      );
    } catch (loginDetailsError) {
      if (
        loginDetailsError?.response?.status === 404 &&
        loginDetailsError?.response?.data?.code === 1002
      ) {
        loginDetails = {
          ...(existingLoginDetails || {}),
          tenantId,
          businessContextId: lastBusinessContext || 'Default'
        };
      } else {
        throw loginDetailsError;
      }
    }
    loginDetails = {...loginDetails, thirdPartyTenantContextPermissions: userToken?.tenantAuthority?.contextPermissionMap};
    if (window.self !== window.top) {
      loginDetails.businessContextId = lastBusinessContext;
    }
    yield put(actions.gettingLoginDetailsSucceeded(loginDetails));
  } catch (ex) {
    console.log(ex);
  }
}